/**
 * Button UI primitive.
 *
 * Supports variant (primary | secondary | danger), size (sm | md | lg),
 * and a loading state that disables the button and shows a spinner.
 *
 * ADR-022: UI Primitives reside in components/ui/.
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  children: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      children,
      className,
      ...props
    },
    ref,
  ) {
    const classes = [
      'btn',
      `btn--${variant}`,
      `btn--${size}`,
      loading ? 'btn--loading' : '',
      className ?? '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        aria-busy={loading}
        {...props}
      >
        {loading && <span className="btn__spinner" aria-hidden="true" />}
        <span className={loading ? 'btn__label--hidden' : 'btn__label'}>
          {children}
        </span>
      </button>
    );
  },
);
